import * as React from "react";
import * as prettyBytes from "pretty-bytes";
import { Package, VitalographUpdateClient } from "@vitalograph/update-client";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { DateTimeAccuracy, LocalizedDateTime } from "../../misc/components/LocalizedDateTime";
import { LocalQueryTable } from "../../misc/components/queryTable/LocalQueryTable";
import { QueryTableColumn } from "../../misc/components/queryTable/QueryTable";
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import MultiSelectModal from "../../misc/components/queryTable/MultiSelectModal";
import { uniqBy } from "lodash";
import { Query } from "../../misc/components/queryTable/Query";
import { QueryCondition } from "../../misc/api/apiTypes";
import { useAboutContextProvider } from "../../Core/AboutContextProvider";

interface UpdatePackagesTableProps {
    updateServiceOffline: boolean | undefined;
    getPackages: () => Promise<void>;
    packages: Package[];
    query: Query<Package>;
    setQuery: React.Dispatch<React.SetStateAction<Query<Package>>>;
}

const UpdatePackagesTable = ({ updateServiceOffline, getPackages, packages, query, setQuery }: UpdatePackagesTableProps) => {
    const [nameFilterVisible, setNameFilterVisible] = React.useState<boolean>(false);
    const [filterNameValues, setFilterNameValues] = React.useState<string[]>([]);

    React.useEffect(() => {
        if (nameFilterVisible) {
            const currentValues = query.Filters.find(f => f.Member === "name")?.Values ?? [];
            setFilterNameValues(currentValues as string[]);
        }
    }, [nameFilterVisible]);

    if (updateServiceOffline === undefined)
        return (<LoadingWithMessage message="Loading Packages..." />);

    if (updateServiceOffline)
        return (<Alert show={true} category={AlertCategoryEnum.Danger}>
            Cannot connect to the Vitalograph Update service <a onClick={() => getPackages()}>Try Again</a>
        </Alert>);

    const updateFilters = (member: string, values: { Label: string; Value: string }[]) => {
        if (values.length === 0) {
            setQuery({
                ...query,
                Data: packages,
                Filters: []
            });
            return;
        }

        setQuery({
            ...query,
            Data: packages.filter(x => values.map(v => v.Value).includes(x[member])),
            Filters: [{
                Member: member,
                Values: values.map(v => v.Value),
                Condition: QueryCondition.Equal,
            }]
        });
    }

    const onFilter = (member: keyof (Package)) => {
        switch (member) {
            case "name":
                setNameFilterVisible(true);
                break;
        }
    }

    const onCloseFilter = () => {
        setNameFilterVisible(false);
    }

    const getNameOptions = () => {
        return uniqBy(packages, p => p.name).map(m => (
            { Label: m.name, Value: m.name }
        ));
    }

    return (
        <>
            <div className="table-responsive">
                <LocalQueryTable collection={query.Data} defaultPageSize={10} rowUniqueIdentifier={row => row.id} query={query} filters={query.Filters} onFilter={onFilter}>
                    <QueryTableColumn member="name" header="Name" map={(row: Package) => row.name} sortable filterable />
                    <QueryTableColumn member="type" header="Type" map={(row: Package) => row.type} />
                    <QueryTableColumn member="version" header="Version" map={(row: Package) => row.version} sortable />
                    <QueryTableColumn member="size" header="Size" map={(row: Package) => prettyBytes(row.size)} />
                    <QueryTableColumn member="created" header="Created Date Time" map={(row: Package) => <LocalizedDateTime time={row.created} accuracy={DateTimeAccuracy.Day} isTooltip />} sortable />
                    <QueryTableColumn member="tags" header="Tags" map={(row: Package) => row.tags} />
                </LocalQueryTable>
            </div>
            <MultiSelectModal
                header="Name Filter"
                show={nameFilterVisible}
                options={getNameOptions()}
                valueKey="Value"
                labelKey="Label"
                values={filterNameValues}
                onChange={values => setFilterNameValues(values.map(v => v.Value))}
                onApply={() => {
                    const valuesToApply = filterNameValues.map(v => ({ Label: v, Value: v }));
                    updateFilters("name", valuesToApply);
                    setNameFilterVisible(false);
                }}
                onClose={onCloseFilter}
                onClear={() => {
                    setFilterNameValues([]);
                    updateFilters("name", []);
                    onCloseFilter();
                }}
            />
        </>
    );
}

export const UpdatePackagesList = () => {
    const [packages, setPackages] = React.useState<Package[]>([]);
    const { about } = useAboutContextProvider();
    const [client, setClient] = React.useState(new VitalographUpdateClient(about.UpdateServiceUri));
    const [updateServiceOffline, setUpdateServiceOffline] = React.useState<boolean | undefined>();
    const [localQuery, setLocalQuery] = React.useState<Query<Package>>({ Data: [], Filters: [] });

    const onClearFilters = () => {
        setLocalQuery({
            ...localQuery,
            Data: packages,
            Filters: []
        });
    }

    const getPackages = async () => {
        try {
            if (!about.UpdateEnabled) {
                setUpdateServiceOffline(true);
                return;
            }

            setUpdateServiceOffline(undefined);
            const packagesResult = await client.package_GetPackages();

            setPackages(packagesResult.data);
            setLocalQuery({
                ...localQuery,
                Data: packagesResult.data,
            });
            setUpdateServiceOffline(false);
        } catch {
            setUpdateServiceOffline(true);
        }
    }

    React.useEffect(() => {
        getPackages();
    }, []);

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Packages</h2>
                </div>
                <div className="col-md-6 h2">
                    <button className="btn btn-primary pull-right" onClick={() => onClearFilters()}>Clear Filters</button>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel">
                        <div className="panel-body">
                            <div className="row">
                                <div className="col-md-12">
                                    <ErrorBoundary>
                                        <UpdatePackagesTable
                                            updateServiceOffline={updateServiceOffline}
                                            getPackages={getPackages}
                                            query={localQuery}
                                            setQuery={setLocalQuery}
                                            packages={packages}
                                        />
                                    </ErrorBoundary>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
